import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { Board } from '../core/Board';
import { PieceBag } from '../core/PieceBag';
import { Scoring } from '../core/Scoring';
import { type PieceData, rotateShape } from '../core/Piece';
import { getProfile, setActiveProfile } from '../profiles/ProfileManager';

const { width, height } = GAME_CONFIG;
const { cellSize, offsetX, offsetY, rows, cols } = GAME_CONFIG.grid;
const { pieces: pieceColors } = GAME_CONFIG.colors;

interface PiecePosition {
  x: number;
  y: number;
  piece: PieceData;
  placed: boolean;
}

export class GameScene extends Phaser.Scene {
  // Core logic
  private board!: Board;
  private pieceBag!: PieceBag;
  private scoring!: Scoring;

  // Graphics layers
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private boardPiecesGraphics!: Phaser.GameObjects.Graphics;
  private ghostGraphics!: Phaser.GameObjects.Graphics;
  private previewGraphics!: Phaser.GameObjects.Graphics;
  private dragGraphics!: Phaser.GameObjects.Graphics;

  // UI texts
  private scoreText!: Phaser.GameObjects.Text;
  private highScoreText!: Phaser.GameObjects.Text;
  private attemptsText!: Phaser.GameObjects.Text;
  private playerNameText!: Phaser.GameObjects.Text;
  private gameOverContainer!: Phaser.GameObjects.Container;
  private finalScoreText!: Phaser.GameObjects.Text;
  private gameOverRecordText!: Phaser.GameObjects.Text;

  // Rotate key (created once)
  private rotateKey!: Phaser.Input.Keyboard.Key;

  // State
  private currentPieces: PiecePosition[] = [];
  private isDragging = false;
  private dragPiece: PieceData | null = null;
  private dragIndex = -1;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private ghostRow = -1;
  private ghostCol = -1;
  private canPlaceGhost = false;
  private isAnimating = false;
  private gameOver = false;

  // Profile and attempts system
  private profileId: string | null = null;
  private attempts = 3;
  private linesCleared = 0;

  // Preview area
  private readonly previewY = GAME_CONFIG.piece.previewY;
  private readonly slotWidth = width / 3;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(data?: { profileId?: string; extraAttempts?: number }): void {
    this.board = new Board();
    this.pieceBag = new PieceBag();
    this.scoring = new Scoring();
    this.currentPieces = [];
    this.isDragging = false;
    this.dragPiece = null;
    this.dragIndex = -1;
    this.isAnimating = false;
    this.gameOver = false;

    // Profile and attempts
    this.profileId = data?.profileId ?? null;
    if (this.profileId !== null) {
      setActiveProfile(this.profileId);
    }
    this.attempts = 3 + (data?.extraAttempts ?? 0);
    this.linesCleared = 0;

    // Graphics layers (draw order = add order)
    this.gridGraphics = this.add.graphics();
    this.boardPiecesGraphics = this.add.graphics();
    this.ghostGraphics = this.add.graphics();
    this.previewGraphics = this.add.graphics();
    this.dragGraphics = this.add.graphics().setDepth(10);

    this.createUI();

    const kb = this.input.keyboard;
    if (kb) {
      this.rotateKey = kb.addKey('R');
    }

    this.generateNewRound();
    this.drawAll();
    this.setupInput();
  }

  private createUI(): void {
    // Player info
    const profile = this.profileId !== null ? getProfile(this.profileId) : null;
    const playerLabel = profile !== null ? profile.name : 'Sin perfil';

    this.playerNameText = this.add.text(10, 8, playerLabel, {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#bcaaa4',
    });

    this.add.text(width / 2, 18, 'EduBlox', {
      fontSize: '30px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffcc80',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    this.scoreText = this.add.text(width / 2, 56, '', {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5, 0);

    this.highScoreText = this.add.text(width / 2, 78, '', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#bcaaa4',
    }).setOrigin(0.5, 0);

    // Attempts display (hearts)
    this.attemptsText = this.add.text(width / 2, 100, '', {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#ef9a9a',
    }).setOrigin(0.5, 0);

    this.updateScoreDisplay();
    this.updateAttemptsDisplay();

    // Back to menu button (small)
    const menuBg = this.add.graphics();
    menuBg.fillStyle(0x757575, 0.7);
    menuBg.fillRoundedRect(width - 72, 8, 64, 24, 6);
    this.add.text(width - 40, 20, 'Menú', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5);

    const menuZone = this.add.zone(width - 40, 20, 64, 24).setInteractive({ useHandCursor: true });
    menuZone.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });

    this.createGameOverUI();
  }

  private createGameOverUI(): void {
    this.gameOverContainer = this.add.container(width / 2, height / 2);
    this.gameOverContainer.setDepth(30);
    this.gameOverContainer.setVisible(false);

    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.85);
    overlay.fillRoundedRect(-170, -150, 340, 300, 16);
    this.gameOverContainer.add(overlay);

    this.gameOverContainer.add(
      this.add.text(0, -120, '¡GAME OVER!', {
        fontSize: '32px',
        fontFamily: 'Arial, sans-serif',
        color: '#ff5252',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    this.finalScoreText = this.add.text(0, -65, '', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    }).setOrigin(0.5);
    this.gameOverContainer.add(this.finalScoreText);

    this.gameOverRecordText = this.add.text(0, -35, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffcc80',
    }).setOrigin(0.5);
    this.gameOverContainer.add(this.gameOverRecordText);

    // Lines cleared
    const linesText = this.add.text(0, -8, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#81c784',
    }).setOrigin(0.5);
    this.gameOverContainer.add(linesText);

    // Stats: {0:{label,value}, ...}

    // "Volver al menú" button
    const menuBtnBg = this.add.graphics();
    menuBtnBg.fillStyle(0x2196f3, 1);
    menuBtnBg.fillRoundedRect(-90, 30, 180, 44, 10);
    this.gameOverContainer.add(menuBtnBg);

    this.gameOverContainer.add(
      this.add.text(0, 52, 'Menú Principal', {
        fontSize: '17px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    const menuBtnZone = this.add.zone(0, 52, 180, 44).setOrigin(0.5);
    menuBtnZone.setInteractive({ useHandCursor: true });
    menuBtnZone.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
    this.gameOverContainer.add(menuBtnZone);

    // "Reintentar" button
    const retryBtnBg = this.add.graphics();
    retryBtnBg.fillStyle(0x4caf50, 1);
    retryBtnBg.fillRoundedRect(-90, 88, 180, 44, 10);
    this.gameOverContainer.add(retryBtnBg);

    this.gameOverContainer.add(
      this.add.text(0, 110, '🔄 Reintentar', {
        fontSize: '17px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5),
    );

    const retryBtnZone = this.add.zone(0, 110, 180, 44).setOrigin(0.5);
    retryBtnZone.setInteractive({ useHandCursor: true });
    retryBtnZone.on('pointerdown', () => {
      this.scene.restart({ profileId: this.profileId });
    });
    this.gameOverContainer.add(retryBtnZone);
  }

  private generateNewRound(): void {
    const pieces = this.pieceBag.generateRound();
    this.currentPieces = pieces.map((piece, i) => ({
      piece,
      x: this.slotWidth * (i + 0.5),
      y: this.previewY,
      placed: false,
    }));
  }

  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.gameOver || this.isAnimating) return;

      for (let i = 0; i < this.currentPieces.length; i++) {
        const pp = this.currentPieces[i];
        if (pp.placed) continue;

        const scale = this.getPreviewScale(pp.piece);
        const pieceW = pp.piece.cols * cellSize * scale;
        const pieceH = pp.piece.rows * cellSize * scale;
        const px = pp.x - pieceW / 2;
        const py = pp.y - pieceH / 2;

        if (
          pointer.x >= px &&
          pointer.x <= px + pieceW &&
          pointer.y >= py &&
          pointer.y <= py + pieceH
        ) {
          this.startDrag(i, pointer);
          return;
        }
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging && this.dragPiece) {
        this.updateDrag(pointer);
      }
    });

    this.input.on('pointerup', () => {
      if (this.isDragging) {
        this.endDrag();
      }
    });
  }

  private startDrag(index: number, pointer: Phaser.Input.Pointer): void {
    const pp = this.currentPieces[index];
    this.isDragging = true;
    this.dragPiece = pp.piece;
    this.dragIndex = index;
    this.dragOffsetX = pointer.x - pp.x;
    this.dragOffsetY = pointer.y - pp.y;
    this.ghostRow = -1;
    this.ghostCol = -1;
    this.canPlaceGhost = false;
  }

  private updateDrag(pointer: Phaser.Input.Pointer): void {
    this.dragGraphics.clear();
    this.ghostGraphics.clear();
    if (!this.dragPiece) return;

    // Piece follows the pointer, preserving the grab offset
    const pieceX = pointer.x - this.dragOffsetX;
    const pieceY = pointer.y - this.dragOffsetY;

    // Top-left corner of the piece
    const topLeftX = pieceX - (this.dragPiece.cols * cellSize) / 2;
    const topLeftY = pieceY - (this.dragPiece.rows * cellSize) / 2;

    const gridW = cols * cellSize;
    const gridH = rows * cellSize;

    const overGrid =
      pieceX >= offsetX - cellSize / 2 &&
      pieceX <= offsetX + gridW + cellSize / 2 &&
      pieceY >= offsetY - cellSize / 2 &&
      pieceY <= offsetY + gridH + cellSize / 2;

    if (overGrid) {
      const rawCol = Math.round((topLeftX - offsetX) / cellSize);
      const rawRow = Math.round((topLeftY - offsetY) / cellSize);
      const col = Math.max(0, Math.min(rawCol, cols - this.dragPiece.cols));
      const row = Math.max(0, Math.min(rawRow, rows - this.dragPiece.rows));

      this.ghostRow = row;
      this.ghostCol = col;
      this.canPlaceGhost = this.board.canPlace(this.dragPiece.shape, row, col);

      // Ghost preview (green when valid, red when invalid)
      const ghostColor = this.canPlaceGhost
        ? this.getPieceColor(this.dragPiece.colorIndex)
        : 0xff5252;
      this.ghostGraphics.fillStyle(ghostColor, this.canPlaceGhost ? 0.45 : 0.3);
      for (let r = 0; r < this.dragPiece.shape.length; r++) {
        for (let c = 0; c < this.dragPiece.shape[r].length; c++) {
          if (this.dragPiece.shape[r][c] === 0) continue;
          const gx = offsetX + (col + c) * cellSize + 1;
          const gy = offsetY + (row + r) * cellSize + 1;
          this.ghostGraphics.fillRect(gx, gy, cellSize - 2, cellSize - 2);
        }
      }
    } else {
      this.ghostRow = -1;
      this.ghostCol = -1;
      this.canPlaceGhost = false;
    }

    this.drawPieceAt(this.dragGraphics, this.dragPiece, pieceX, pieceY, 0.85, 1);
  }

  private endDrag(): void {
    const piece = this.dragPiece;
    const index = this.dragIndex;

    this.isDragging = false;
    this.dragPiece = null;
    this.dragIndex = -1;
    this.dragGraphics.clear();
    this.ghostGraphics.clear();

    if (!piece || index < 0) {
      this.drawAll();
      return;
    }

    if (this.canPlaceGhost && this.ghostRow >= 0 && this.ghostCol >= 0) {
      this.board.placePiece(piece.shape, this.ghostRow, this.ghostCol, piece.colorIndex + 1);
      this.currentPieces[index].placed = true;
      this.checkAndClearLines();
    } else {
      // Piece returns to its slot
      this.drawAll();
    }
  }

  private checkAndClearLines(): void {
    const lines = this.board.checkLines();
    const totalLines = lines.rows.length + lines.cols.length;

    if (totalLines > 0) {
      this.isAnimating = true;
      this.linesCleared += totalLines;
      this.animateLineClear(lines, () => {
        this.board.clearLines(lines);

        const points = this.scoring.calculatePoints(totalLines);
        this.scoring.addPoints(points);
        this.showFloatingText(`+${points}`, width / 2, offsetY - 20);

        if (this.board.isBoardEmpty()) {
          this.showCleanBoardBonus();
        }

        this.isAnimating = false;
        this.updateScoreDisplay();
        this.drawAll();
        this.checkRoundCompletion();
      });
    } else {
      this.updateScoreDisplay();
      this.drawAll();
      this.checkRoundCompletion();
    }
  }

  private showCleanBoardBonus(): void {
    // Clean board: apply ×5 before adding line points (bonus on the current score)
    const currentScore = this.scoring.getScore();
    const bonusPoints = currentScore * (GAME_CONFIG.scoring.cleanBoardMult - 1);

    // Animate the X5
    const bonusBg = this.add.graphics();
    bonusBg.fillStyle(0x1b5e20, 0.9);
    bonusBg.fillRoundedRect(width / 2 - 80, offsetY - 90, 160, 50, 12);
    bonusBg.setDepth(25);

    const bonusText = this.add.text(width / 2, offsetY - 65, '¡x5 BONUS!', {
      fontSize: '24px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffeb3b',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(25);

    this.showFloatingText(`+${bonusPoints}`, width / 2, offsetY - 120);

    this.tweens.add({
      targets: [bonusBg, bonusText],
      scaleX: 1.3,
      scaleY: 1.3,
      yoyo: true,
      duration: 300,
      ease: 'Back.easeInOut',
      onComplete: () => {
        this.scoring.applyCleanBoardMultiplier();

        // Flash effect
        const flash = this.add.graphics().setDepth(26);
        flash.fillStyle(0xffffff, 0.6);
        flash.fillRect(0, 0, width, height);
        this.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 300,
          onComplete: () => flash.destroy(),
        });

        this.tweens.add({
          targets: [bonusBg, bonusText],
          alpha: 0,
          delay: 600,
          duration: 400,
          onComplete: () => {
            bonusBg.destroy();
            bonusText.destroy();
          },
        });

        this.updateScoreDisplay();
      },
    });
  }

  private animateLineClear(
    lines: { rows: number[]; cols: number[] },
    onComplete: () => void,
  ): void {
    const flashGraphics = this.add.graphics().setDepth(15);
    let flashes = 0;

    const flash = (): void => {
      flashGraphics.clear();

      if (flashes % 2 === 0) {
        flashGraphics.fillStyle(0xffffff, 0.65);
        for (const r of lines.rows) {
          flashGraphics.fillRect(offsetX, offsetY + r * cellSize, cols * cellSize, cellSize);
        }
        for (const c of lines.cols) {
          flashGraphics.fillRect(offsetX + c * cellSize, offsetY, cellSize, rows * cellSize);
        }
      }

      flashes++;
      if (flashes >= 4) {
        flashGraphics.destroy();
        onComplete();
      } else {
        this.time.delayedCall(80, flash);
      }
    };

    flash();
  }

  private showFloatingText(text: string, x: number, y: number): void {
    const floatText = this.add.text(x, y, text, {
      fontSize: '28px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffeb3b',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(25);

    this.tweens.add({
      targets: floatText,
      y: y - 60,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => {
        floatText.destroy();
      },
    });
  }

  private checkRoundCompletion(): void {
    const allPlaced = this.currentPieces.every((pp) => pp.placed);

    if (allPlaced) {
      this.generateNewRound();
      this.drawAll();
      if (this.board.isGameOver(this.currentPieces.map((pp) => pp.piece.shape))) {
        this.handleNoMovesLeft();
      }
      return;
    }

    const remaining = this.currentPieces
      .filter((pp) => !pp.placed)
      .map((pp) => pp.piece.shape);
    if (this.board.isGameOver(remaining)) {
      this.handleNoMovesLeft();
    }
  }

  private handleNoMovesLeft(): void {
    if (this.attempts > 0) {
      // Lose one attempt and go to challenges
      this.attempts--;
      this.updateAttemptsDisplay();

      // Show "no moves" feedback
      const feedback = this.add.text(width / 2, offsetY + rows * cellSize + 30, '😅 ¡Sin movimientos!', {
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffcc80',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(25);

      this.tweens.add({
        targets: feedback,
        alpha: 0,
        y: feedback.y - 30,
        delay: 800,
        duration: 500,
        onComplete: () => feedback.destroy(),
      });

      if (this.attempts <= 0) {
        // Game over — no more attempts
        this.time.delayedCall(1000, () => this.triggerGameOver());
      } else {
        // Send player to challenges to earn more attempts
        this.time.delayedCall(1200, () => {
          this.scene.start('ChallengeScene', {
            profileId: this.profileId,
            score: this.scoring.getScore(),
            linesCleared: this.linesCleared,
          });
        });
      }
    }
  }

  private triggerGameOver(): void {
    this.gameOver = true;
    this.dragGraphics.clear();
    this.ghostGraphics.clear();

    const score = this.scoring.getScore();
    const highScore = this.scoring.getHighScore();
    const newRecord = score > 0 && score >= highScore;

    // Save result to profile
    if (this.profileId !== null) {
      const profile = getProfile(this.profileId);
      if (profile !== null) {
        // We transition to GameOverScene which handles saving
      }
    }

    // Navigate to GameOverScene
    this.scene.start('GameOverScene', {
      score,
      highScore,
      linesCleared: this.linesCleared,
      newRecord,
      profileId: this.profileId,
    });
  }

  private updateScoreDisplay(): void {
    this.scoreText.setText(`Puntuación: ${this.scoring.getScore()}`);
    this.highScoreText.setText(`Mejor: ${this.scoring.getHighScore()}`);
  }

  private updateAttemptsDisplay(): void {
    const hearts = '❤️'.repeat(Math.max(0, this.attempts));
    const empties = '🤍'.repeat(Math.max(0, 3 - this.attempts));
    this.attemptsText.setText(`Intentos: ${hearts}${empties}`);
  }

  private drawAll(): void {
    this.drawGrid();
    this.drawBoardPieces();
    this.drawPreviewPieces();
  }

  private drawGrid(): void {
    this.gridGraphics.clear();

    // Full background
    this.gridGraphics.fillStyle(GAME_CONFIG.colors.background, 1);
    this.gridGraphics.fillRect(0, 0, width, height);

    // Grid frame (line color 0x5d4037)
    this.gridGraphics.fillStyle(GAME_CONFIG.colors.grid, 1);
    this.gridGraphics.fillRect(offsetX, offsetY, cols * cellSize, rows * cellSize);

    // Empty cells (0x4e342e), 1px gap shows the lines
    this.gridGraphics.fillStyle(GAME_CONFIG.colors.cellEmpty, 1);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.gridGraphics.fillRect(
          offsetX + c * cellSize + 1,
          offsetY + r * cellSize + 1,
          cellSize - 2,
          cellSize - 2,
        );
      }
    }
  }

  private drawBoardPieces(): void {
    this.boardPiecesGraphics.clear();

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.board.grid[r][c];
        if (cell === 0) continue;

        const colorIndex = cell - 1;
        this.boardPiecesGraphics.fillStyle(this.getPieceColor(colorIndex), 1);
        this.boardPiecesGraphics.fillRoundedRect(
          offsetX + c * cellSize + 2,
          offsetY + r * cellSize + 2,
          cellSize - 4,
          cellSize - 4,
          4,
        );
      }
    }
  }

  private drawPreviewPieces(): void {
    this.previewGraphics.clear();

    for (const pp of this.currentPieces) {
      if (pp.placed) continue;
      const scale = this.getPreviewScale(pp.piece);
      this.drawPieceAt(this.previewGraphics, pp.piece, pp.x, pp.y, 1, scale);
    }
  }

  private getPreviewScale(piece: PieceData): number {
    const maxPieceW = piece.cols * cellSize;
    const scale = (this.slotWidth - 8) / maxPieceW;
    return Math.min(1, scale);
  }

  private drawPieceAt(
    graphics: Phaser.GameObjects.Graphics,
    piece: PieceData,
    centerX: number,
    centerY: number,
    alpha: number,
    scale: number,
  ): void {
    const pieceW = piece.cols * cellSize * scale;
    const pieceH = piece.rows * cellSize * scale;
    const startX = centerX - pieceW / 2;
    const startY = centerY - pieceH / 2;

    // Drop shadow
    graphics.fillStyle(0x000000, 0.25 * alpha);
    graphics.fillRoundedRect(startX - 2, startY - 2, pieceW + 4, pieceH + 4, 6);

    const color = this.getPieceColor(piece.colorIndex);

    for (let r = 0; r < piece.shape.length; r++) {
      for (let c = 0; c < piece.shape[r].length; c++) {
        if (piece.shape[r][c] === 0) continue;

        const x = startX + c * cellSize * scale;
        const y = startY + r * cellSize * scale;
        const w = cellSize * scale;

        graphics.fillStyle(color, alpha);
        graphics.fillRoundedRect(x + 2, y + 2, w - 4, w - 4, 4);

        // Top highlight
        graphics.fillStyle(0xffffff, 0.15 * alpha);
        graphics.fillRoundedRect(x + 2, y + 2, w - 4, 3, 2);
        graphics.fillRoundedRect(x + 2, y + 2, 3, w - 4, 2);
      }
    }
  }

  private getPieceColor(colorIndex: number): number {
    if (colorIndex >= 0 && colorIndex < pieceColors.length) {
      return pieceColors[colorIndex];
    }
    return 0xffffff;
  }

  update(): void {
    if (!this.isDragging || !this.dragPiece) return;

    if (Phaser.Input.Keyboard.JustDown(this.rotateKey)) {
      this.dragPiece.shape = rotateShape(this.dragPiece.shape);
      this.dragPiece.rows = this.dragPiece.shape.length;
      this.dragPiece.cols = this.dragPiece.shape[0].length;
      this.updateDrag(this.input.activePointer);
    }
  }
}